import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { ServiceModules } from '@/lib/constants/services'

// ============================================
// ASYNC THUNKS
// ============================================

export interface FetchClientMetaParams {
    orgId: string
    api: {
        fetcher: <T = any>(url: string, config: { service: any; queryKey: string[] }) => Promise<T>
    }
}

export const fetchClientMeta = createAsyncThunk(
    'clientDetails/fetchMeta',
    async ({ orgId, api }: FetchClientMetaParams) => {
        const [
            timezones,
            countries,
            companySizes,
            clientValues,
            difficultyLevels,
            employmentTypes,
            compensationPeriods,
            workplaces,
            scheduleTypes,
            hireTenures,
            emailTemplates,
            currencies,
            industries,
        ] = await Promise.all([
            api.fetcher<Timezone[]>('/timezones', {
                service: ServiceModules.CLIENT,
                queryKey: ['timezones'],
            }),
            api.fetcher<Country[]>('/metadata/index?model=countries', {
                service: ServiceModules.MAIN,
                queryKey: ['countries'],
            }),
            api.fetcher<ClientCompanySize[]>('/enums/company-size', {
                service: ServiceModules.CLIENT,
                queryKey: ['company-size'],
            }),
            api.fetcher<ClientValueRow[]>('/enums/client-value', {
                service: ServiceModules.CLIENT,
                queryKey: ['client-value'],
            }),
            api.fetcher<PositionDifficultyLevel[]>('/enums/difficulty-level', {
                service: ServiceModules.CLIENT,
                queryKey: ['difficulty-level'],
            }),
            api.fetcher<PositionEmploymentType[]>('/enums/employment-type', {
                service: ServiceModules.CLIENT,
                queryKey: ['employment-type'],
            }),
            api.fetcher<PositionCompensationPeriod[]>('/enums/compensation-period', {
                service: ServiceModules.CLIENT,
                queryKey: ['compensation-period'],
            }),
            api.fetcher<PositionWorkplace[]>('/enums/workplace', {
                service: ServiceModules.CLIENT,
                queryKey: ['workplace'],
            }),
            api.fetcher<PositionScheduleType[]>('/enums/schedule-type', {
                service: ServiceModules.CLIENT,
                queryKey: ['schedule-type'],
            }),
            api.fetcher<PositionHireTenure[]>('/enums/hire-tenure', {
                service: ServiceModules.CLIENT,
                queryKey: ['hire-tenure'],
            }),
            api.fetcher<{ rows: EmailTemplateRow[] }>(`/organization/${orgId}/email-templates?perPage=100&sort=subject`, {
                service: ServiceModules.NOTIFICATION,
                queryKey: ['email-templates', orgId],
            }),
            api.fetcher<Currency[]>('/metadata/index?model=currencies', {
                service: ServiceModules.MAIN,
                queryKey: ['currencies'],
            }),
            api.fetcher<ClientAvailableJobFieldsDropdownRow[]>('/metadata/index?model=available_job_fields', {
                service: ServiceModules.MAIN,
                queryKey: ['industries'],
            }),
        ])

        return {
            timezones,
            countries,
            companySizes,
            clientValues,
            difficultyLevels,
            employmentTypes,
            compensationPeriods,
            workplaces,
            scheduleTypes,
            hireTenures,
            emailTemplates: emailTemplates?.rows,
            currencies,
            industries,
        }
    }
)

// ============================================
// MAIN STATE
// ============================================

export interface ClientDetailsState {
    // Meta
    clientId: string | null
    isLoading: boolean

    // Client Data
    clientProfile: ClientProfileDetails | null
    profileRefetchTrigger: number
    notesRefetchTrigger: number
    filesRefetchTrigger: number
    notes: ClientNote[]
    files: ClientProfileFile[]
    meta: {
        timezones: Timezone[] | undefined;
        countries: Country[] | undefined;
        companySizes: ClientCompanySize[] | undefined;
        clientValues: ClientValueRow[] | undefined;
        difficultyLevels: PositionDifficultyLevel[] | undefined;
        employmentTypes: PositionEmploymentType[] | undefined;
        compensationPeriods: PositionCompensationPeriod[] | undefined;
        workplaces: PositionWorkplace[] | undefined;
        scheduleTypes: PositionScheduleType[] | undefined;
        hireTenures: PositionHireTenure[] | undefined;
        emailTemplates: EmailTemplateRow[] | undefined;
        currencies: Currency[] | undefined;
        industries: ClientAvailableJobFieldsDropdownRow[] | undefined;
    } | null
}

// ============================================
// INITIAL STATE
// ============================================

const initialState: ClientDetailsState = {
    clientId: null,
    isLoading: false,
    clientProfile: null,
    profileRefetchTrigger: 0,
    notesRefetchTrigger: 0,
    filesRefetchTrigger: 0,
    notes: [],
    files: [],
    meta: null
}

// ============================================
// SLICE
// ============================================

const clientDetailsSlice = createSlice({
    name: 'clientDetails',
    initialState,
    reducers: {
        // Meta
        setClientId: (state, action: PayloadAction<string | null>) => {
            state.clientId = action.payload
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },
        resetClientDetails: () => initialState,

        // Client Profile
        setClientProfile: (state, action: PayloadAction<ClientProfileDetails>) => {
            state.clientProfile = action.payload
        },
        updateClientProfile: (state, action: PayloadAction<Partial<ClientProfileDetails>>) => {
            if (state.clientProfile) {
                state.clientProfile = { ...state.clientProfile, ...action.payload }
            }
        },
        triggerProfileRefetch: (state) => {
            state.profileRefetchTrigger += 1
        },

        // Notes
        setNotes: (state, action: PayloadAction<ClientNote[]>) => {
            state.notes = action.payload
        },
        addNote: (state, action: PayloadAction<ClientNote>) => {
            state.notes = [action.payload, ...state.notes]
        },
        updateNote: (state, action: PayloadAction<ClientNote>) => {
            const index = state.notes.findIndex(n => n.id === action.payload.id)
            if (index !== -1) {
                state.notes[index] = action.payload
            }
        },
        deleteNote: (state, action: PayloadAction<number>) => {
            state.notes = state.notes.filter(n => n.id !== action.payload)
        },
        triggerNotesRefetch: (state) => {
            state.notesRefetchTrigger += 1
        },

        // Files
        setFiles: (state, action: PayloadAction<ClientProfileFile[]>) => {
            state.files = action.payload
        },
        addFile: (state, action: PayloadAction<ClientProfileFile>) => {
            state.files.push(action.payload)
        },
        deleteFile: (state, action: PayloadAction<string>) => {
            state.files = state.files.filter(f => f.id !== action.payload)
        },
        triggerFilesRefetch: (state) => {
            state.filesRefetchTrigger += 1
        },

        // Meta
        setMeta: (state, action: PayloadAction<ClientDetailsState['meta']>) => {
            state.meta = action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchClientMeta.fulfilled, (state, action) => {
            state.meta = action.payload
        })
    },
})

// Export actions
export const {
    // Meta
    setClientId,
    setIsLoading,
    resetClientDetails,

    // Client Profile
    setClientProfile,
    updateClientProfile,
    triggerProfileRefetch,

    // Notes
    setNotes,
    addNote,
    updateNote,
    deleteNote,
    triggerNotesRefetch,

    // Files
    setFiles,
    addFile,
    deleteFile,
    triggerFilesRefetch,

    // Meta
    setMeta,
} = clientDetailsSlice.actions

// Export reducer
export default clientDetailsSlice.reducer
